/**
 * FareHistoryChart — one line per fare type for one flight (P7). Each line is labelled at
 * its end with its name and last price, so colour never carries identity alone. A break in
 * a line is a check where that fare was not on sale. Tap a point for its date and price.
 *
 * The sentence on top comes from the server (services/fareHistory.ts), the same words the
 * trip email uses.
 */
import { useState } from "react";
import type { FareHistoryView } from "@/lib/types";
import { chartLayout, money, shortDay, type ChartPoint } from "./fareHistoryChartView";

/** The validated categorical palette, in its fixed order, light then dark. Follows the fare, not its rank. */
const SERIES = [
  { stroke: "stroke-[#2a78d6] dark:stroke-[#3987e5]", fill: "fill-[#2a78d6] dark:fill-[#3987e5]" },
  { stroke: "stroke-[#eb6834] dark:stroke-[#d95926]", fill: "fill-[#eb6834] dark:fill-[#d95926]" },
  { stroke: "stroke-[#1baf7a] dark:stroke-[#199e70]", fill: "fill-[#1baf7a] dark:fill-[#199e70]" },
  { stroke: "stroke-[#eda100] dark:stroke-[#c98500]", fill: "fill-[#eda100] dark:fill-[#c98500]" },
  { stroke: "stroke-[#e87ba4] dark:stroke-[#d55181]", fill: "fill-[#e87ba4] dark:fill-[#d55181]" },
  { stroke: "stroke-[#008300] dark:stroke-[#008300]", fill: "fill-[#008300] dark:fill-[#008300]" },
  { stroke: "stroke-[#6250d6] dark:stroke-[#9085e9]", fill: "fill-[#6250d6] dark:fill-[#9085e9]" },
  { stroke: "stroke-[#e34948] dark:stroke-[#e66767]", fill: "fill-[#e34948] dark:fill-[#e66767]" },
];

const pathOf = (run: ChartPoint[]) => run.map((p, i) => (i ? "L" : "M") + p.x.toFixed(1) + " " + p.y.toFixed(1)).join(" ");

export default function FareHistoryChart({ view }: { view: FareHistoryView }) {
  const [picked, setPicked] = useState<{ level: string; at: string; price: number } | null>(null);
  const hasLines = view.series.some((s) => s.points.some((p) => p.price != null));

  return (
    <div className="space-y-2 text-xs text-card-foreground" data-fare-history-chart>
      <p className="font-medium text-foreground">{view.summary}</p>
      {view.justStarted && <p className="text-muted-foreground">{view.justStarted}</p>}

      {view.checks >= 2 && hasLines && (() => {
        const c = chartLayout(view.series);
        return (
          <>
            <p aria-live="polite" className="min-h-[1.25rem] text-muted-foreground">
              {picked
                ? `${shortDay(picked.at)}, ${picked.level}: ${money(picked.price)} per person`
                : "Tap a point for its date and price."}
            </p>
            <svg
              viewBox={`0 0 ${c.width} ${c.height}`}
              width="100%"
              style={{ maxWidth: c.width }}
              role="img"
              aria-label={"Price by fare type. " + view.summary}
              className="block overflow-visible"
            >
              {c.yTicks.map((t) => (
                <g key={t.label}>
                  <line x1={c.plot.left} x2={c.plot.right} y1={t.y} y2={t.y} className="stroke-border" strokeWidth="1" />
                  <text x={c.plot.left - 4} y={t.y + 3.5} textAnchor="end" className="fill-muted-foreground text-[10px] tabular-nums">
                    {t.label}
                  </text>
                </g>
              ))}
              {c.xLabels.map((t) => (
                <text key={t.label} x={t.x} y={c.height - 6} textAnchor={t.anchor} className="fill-muted-foreground text-[10px]">
                  {t.label}
                </text>
              ))}
              {c.lines.map((l, i) => {
                const color = SERIES[i % SERIES.length];
                const end = l.points[l.points.length - 1];
                return (
                  <g key={l.key} data-fare-line={l.level}>
                    {l.runs.map((run, r) =>
                      run.length > 1 ? (
                        <path key={r} d={pathOf(run)} fill="none" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" className={color.stroke} />
                      ) : null,
                    )}
                    {l.points.map((p) =>
                      l.showDots || p.alone ? (
                        <circle key={p.at} cx={p.x} cy={p.y} r="3.5" strokeWidth="1.5" className={color.fill + " stroke-card"} />
                      ) : null,
                    )}
                    {end && (
                      <line x1={end.x} y1={end.y} x2={c.plot.right + 6} y2={l.label.y} strokeWidth="1" strokeDasharray="1 2" className={color.stroke} />
                    )}
                    <circle cx={c.plot.right + 10} cy={l.label.y - 4} r="3.5" className={color.fill} />
                    <text x={c.plot.right + 17} y={l.label.y} className="fill-foreground text-[11px]">
                      {l.label.name}
                    </text>
                    <text x={c.plot.right + 17} y={l.label.y + 12} className="fill-muted-foreground text-[11px] tabular-nums">
                      {l.label.price}
                    </text>
                    {/* Tap targets, far larger than the dot. */}
                    {l.points.map((p) => (
                      <circle
                        key={"t" + p.at}
                        cx={p.x}
                        cy={p.y}
                        r="14"
                        fill="transparent"
                        role="button"
                        tabIndex={0}
                        aria-label={`${shortDay(p.at)}, ${l.level}, ${money(p.price)}`}
                        className="cursor-pointer outline-none"
                        onClick={() => setPicked({ level: l.level, at: p.at, price: p.price })}
                        onMouseEnter={() => setPicked({ level: l.level, at: p.at, price: p.price })}
                        onFocus={() => setPicked({ level: l.level, at: p.at, price: p.price })}
                      />
                    ))}
                  </g>
                );
              })}
            </svg>
            {c.hasGap && <p className="text-muted-foreground">A break in a line means that fare was not on sale at that check.</p>}
            {c.foldedOut > 0 && <p className="text-muted-foreground">{c.foldedOut} more fare types are in the numbers below.</p>}
            <details>
              <summary className="flex min-h-[44px] cursor-pointer items-center text-muted-foreground sm:min-h-[28px]">See the numbers</summary>
              <div className="max-h-56 overflow-auto rounded border">
                <table className="w-full text-left tabular-nums">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="px-2 py-1 font-medium">Fare</th>
                      <th className="px-2 py-1 font-medium">Check</th>
                      <th className="px-2 py-1 text-right font-medium">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.series.flatMap((s) =>
                      s.points.map((p) => (
                        <tr key={s.cabin + s.level + p.at} className="border-t">
                          <td className="px-2 py-1">{s.level}</td>
                          <td className="px-2 py-1">{shortDay(p.at)}</td>
                          <td className="px-2 py-1 text-right">{p.price == null ? "Not sold" : money(p.price)}</td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        );
      })()}

      <p className="text-muted-foreground">
        Per person, for this flight on this date.
        {view.olderDateChecks > 0 && ` We also kept ${view.olderDateChecks} checks from an earlier date of this flight.`}
      </p>
    </div>
  );
}
